import React, { useCallback, useEffect } from 'react';
import {
  ReactFlow, Background, Controls, MiniMap,
  BackgroundVariant, useOnViewportChange,
} from '@xyflow/react';
import { useERDStore, selectNodes, selectEdges } from './erdStore';
import ERDNode from './ERDNode';
import RelationshipEdge from './RelationshipEdge';
import { RELATIONSHIP_CONFIG } from './erdConstants';

const nodeTypes = {
  collection:    ERDNode,
  subcollection: ERDNode,
  external:      ERDNode,
  process:       ERDNode,
  note:          ERDNode,
};

const edgeTypes = {
  relationship: RelationshipEdge,
};

const SNAP_GRID = [20, 20];

export default function FlowCanvas() {
  const nodes = useERDStore(selectNodes);
  const edges = useERDStore(selectEdges);
  const snapToGrid = useERDStore(s => s.snapToGrid);
  const {
    onNodesChange, onEdgesChange, onConnect,
    setViewport, setSelection,
    deleteNode, deleteEdge, duplicateNode, copySelectedNode, pasteNode,
    selectedElementId, selectedElementType,
    saveDocument,
  } = useERDStore();

  useOnViewportChange({
    onChange: (vp) => setViewport(vp),
  });

  useEffect(() => {
    function onKey(e) {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        saveDocument().catch(() => {});
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        if (selectedElementId && selectedElementType === 'node') {
          duplicateNode(selectedElementId);
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        copySelectedNode();
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        pasteNode();
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElementId) {
        if (selectedElementType === 'node') deleteNode(selectedElementId);
        if (selectedElementType === 'edge') deleteEdge(selectedElementId);
      }
      if (e.key === 'Escape') {
        setSelection(null, null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedElementId, selectedElementType, saveDocument, deleteNode, deleteEdge, duplicateNode, copySelectedNode, pasteNode, setSelection]);

  const onNodeClick = useCallback((_e, node) => setSelection(node.id, 'node'), [setSelection]);
  const onEdgeClick = useCallback((_e, edge) => setSelection(edge.id, 'edge'), [setSelection]);
  const onPaneClick = useCallback(() => setSelection(null, null), [setSelection]);

  const theme = document.documentElement.getAttribute('data-theme') || 'dark';
  const isDark = theme === 'dark';

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.1}
        maxZoom={2}
        deleteKeyCode={null}
        multiSelectionKeyCode="Control"
        selectionOnDrag
        connectionRadius={30}
        defaultEdgeOptions={{ type: 'relationship' }}
        snapToGrid={snapToGrid}
        snapGrid={SNAP_GRID}
      >
        <Background
          variant={snapToGrid ? BackgroundVariant.Lines : BackgroundVariant.Dots}
          color={isDark ? '#1e293b' : '#cbd5e1'}
          gap={20}
          size={snapToGrid ? 0.5 : 1.5}
        />
        <Controls
          style={{
            background: isDark ? '#1e293b' : '#ffffff',
            border: `1px solid ${isDark ? '#334155' : '#d0d7de'}`,
            borderRadius: 8,
          }}
        />
        <MiniMap
          style={{
            background: isDark ? '#1e293b' : '#ffffff',
            border: `1px solid ${isDark ? '#334155' : '#d0d7de'}`,
          }}
          nodeColor={isDark ? '#334155' : '#cbd5e1'}
          maskColor={isDark ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.7)'}
        />

        <svg style={{ position: 'absolute', width: 0, height: 0 }}>
          <defs>
            {Object.entries(RELATIONSHIP_CONFIG).map(([type, cfg]) => (
              <marker key={type} id={`arrow-${type}`} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <path d="M0,0 L0,6 L8,3 z" fill={cfg.color} />
              </marker>
            ))}
          </defs>
        </svg>
      </ReactFlow>

      {/* Page stats overlay */}
      <div className="erd-canvas-stats">
        {nodes.length} node{nodes.length !== 1 ? 's' : ''} &middot; {edges.length} edge{edges.length !== 1 ? 's' : ''}
        {snapToGrid && <> &middot; Snap</>}
      </div>
    </>
  );
}
